<template>
    <div v-if="can" class="pb-32">
        <settings-heading
            :title="$t('settings.invoices.title')"
            :description="$t('settings.invoices.description')"
        />

        <loading v-if="fetchingInvoices"/>
        <template v-else>
            <!-- Unpaid invoices -->
            <template v-if="openInvoices.length > 0">
                <banner
                    :title="$t('settings.invoices.unpaid_title')"
                    :description="$tc('settings.invoices.unpaid_description', openInvoices.length)"
                    danger
                    inline
                    :one-line="false"
                />

                <div class="shadow-xs border border-primary-5 overflow-hidden sm:rounded-6 mb-48">
                    <table class="w-full divide-y divide-primary-20">
                        <tbody class="divide-y divide-primary-20" x-max="1">
                        <tr v-for="invoice in openInvoices">
                            <td class="px-16 py-14 whitespace-nowrap text-primary-80">
                                {{ formatDate(invoice.created_at) }}
                            </td>
                            <td class="px-16 py-14 flex items-center" :class="invoice.is_expired ? 'text-semantic-error font-semibold' : 'text-primary-80'">
                                <svg-icon v-if="invoice.is_expired" class="max-w-16 max-h-16 text-semantic-error mr-3" type="warning"></svg-icon>
                                {{ invoice.title }}
                            </td>
                            <td class="px-16 py-14 whitespace-nowrap" :class="invoice.is_expired ? 'text-semantic-error font-semibold' : 'text-primary-80'">
                                <div class="inline-flex items-center">
                                    <currency/>{{ invoice.total }}
                                </div>
                            </td>
                            <td class="px-16 py-14 whitespace-nowrap text-primary-80">
                                <tag
                                    display="danger"
                                >
                                    {{ invoice.is_expired ? $t('settings.invoices.statuses.expired') : $t('settings.invoices.statuses.unpaid') }}
                                </tag>
                            </td>
                            <td class="px-16 py-14 whitespace-nowrap text-right">
                                <button class="inline-flex items-center py-4 px-10 font-semibold rounded-32 bg-primary-20 text-white hover:text-primary-80 trans-ease"
                                        :class="sendingMail ? 'cursor-wait' : ''"
                                        @click="sendMail(invoice)" :disabled="sendingMail">
                                    <svg-icon class="max-w-16 max-h-16 mr-8" type="replay"></svg-icon>
                                    <span>{{ $t('general.resend_mail') }}</span>
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <!--
            Indien er geen onbetaalde facturen meer zijn.
                -->
            <div v-else class="rounded-6 w-2/3 p-32 pt-24 mt-16 mx-auto mt-32 text-center">
                <div class="mb-32 w-288 inline-block">
                    <lottie-animation
                        :autoPlay="true"
                        :loop="false"
                        :speed="0.7"
                        :animationData="animation"
                    />
                </div>

                <h3 class="block text-body text-center leading-5">
                    {{ $t('settings.invoices.no_outstanding_invoices') }}</h3>
                <div v-if="invoices.length" class="mt-32 text-center">
                    <simple-button
                        look="primary"
                        type="link"
                        :href="invoices[0].pdf"
                        target="_blank"
                        class="mx-auto"
                    >
                        {{ $t('settings.invoices.view_latest') }}
                    </simple-button>
                </div>
            </div>


            <div class="shadow-xs border border-primary-5 overflow-hidden sm:rounded-6 my-48">
                <table class="w-full divide-y divide-primary-20">
                    <thead class="bg-primary-5">
                    <tr>
                        <th class="px-16 py-10 text-left text-primary-80 tracking-wider capitalize" scope="col">
                            {{ $t('general.date.title') }}
                        </th>
                        <th class="px-8 py-10 text-left text-primary-80 tracking-wider capitalize" scope="col">
                            {{ $t('settings.invoices.expiry_date') }}
                        </th>
                        <th class="px-16 py-10 text-left text-primary-80 tracking-wider capitalize" scope="col">
                            {{ $t('admin.jobboards.fields.description') }}
                        </th>
                        <th class="px-16 py-10 text-left text-primary-80 tracking-wider capitalize" scope="col">
                            {{ $t('general.amount') }}
                        </th>
                        <th class="px-16 py-10 text-left text-primary-80 tracking-wider capitalize" scope="col">
                            {{ $t('general.status') }}
                        </th>
                        <!--
                          `relative` is added here due to a weird bug in Safari that causes `sr-only` headings to introduce overflow on the body on mobile.
                        -->
                        <th class="relative px-16 py-8 text-left text-body-sm text-primary-80 tracking-wider" scope="col">
                            <span class="sr-only">{{ $t('general.view') }}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-primary-20" x-max="1">

                    <tr v-for="invoice in invoices" v-if="invoices.length" :key="invoice.id">
                        <td class="px-16 py-14 whitespace-nowrap text-primary-80">
                            {{ formatDate(invoice.created_at) }}
                        </td>
                        <td class="px-8 py-14 whitespace-nowrap text-primary-80">
                            {{ formatDate(invoice.due_date) }}
                        </td>
                        <td class="px-16 py-14 text-primary-80">
                            {{ invoice.title }}
                        </td>
                        <td class="px-16 py-14 whitespace-nowrap text-primary-80">
                            <currency/>{{ invoice.total }}
                        </td>
                        <td class="px-16 py-14 whitespace-nowrap text-primary-80">
                            <tag
                                v-if="invoice.is_creditnote"
                                display="info"
                            >
                                {{ $t('settings.invoices.statuses.credit_note') }}
                            </tag>
                            <tag
                                v-else-if="invoice.state === 'PAID'"
                                display="success"
                            >
                                {{ $t('settings.invoices.statuses.paid') }}
                            </tag>
                            <tag
                                v-else-if="['PENDING', 'BOOKED'].includes(invoice.state)"
                                display="warning"
                                icon="replay"
                            >
                                {{ $t('settings.invoices.statuses.pending') }}
                            </tag>
                            <tag
                                v-else-if="invoice.state === 'EXPIRED'"
                                display="danger"
                            >
                                {{ $t('settings.invoices.statuses.expired') }}
                            </tag>
                            <tag
                                v-else-if="invoice.state === 'ARCHIVED'"
                                display="primary"
                                disabled
                            >
                                {{ $t('settings.invoices.statuses.archived') }}
                            </tag>
                        </td>
                        <td class="px-16 py-14 whitespace-nowrap text-right">
                            <a :href="invoice.pdf"
                               class="hover:text-primary-80 text-primary-80 capitalize"
                               target="_blank">
                                {{ $t('general.view') }}
                            </a>
                        </td>
                    </tr>
                    <tr v-if="!invoices.length">
                        <td class="px-16 py-14 whitespace-nowrap text-center text-primary-80" colspan="5">
                            {{ $t('settings.invoices.no_invoices_found') }}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import Error403 from '../../components/ui/Error403.vue';
import 'vue-range-slider/dist/vue-range-slider.css';
import BillingFields from '../../components/settings/BillingFields';
import SvgIcon from '../../components/ui/SvgIcon';
import SimpleButtonVue from '../../components/ui/SimpleButton';
import * as invoices from '../../services/InvoiceService';
import Invoice from '../../models/Invoice';
import {normalizeJson} from '../../../shared/helpers';
import Loading from '../../components/ui/Loading';
import moment from 'moment';
import {ADD_NOTIFICATION} from "../../state/mutation-types";
import {LottieAnimation} from "lottie-web-vue";
import animation from '../../../../../../public/interim/animations/success.json';
import Currency from '../../components/Currency';
import Tag from "../../components/ui/Tag.vue";
import Banner from "../../components/ui/Banner.vue";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        Banner,
        Tag,
        Loading,
        BillingFields,
        'error-403': Error403,
        'svg-icon': SvgIcon,
        'simple-button': SimpleButtonVue,
        LottieAnimation,
        Currency,
    },
    data: function () {
        return {
            fetchingInvoices: false,
            sendingMail: false,
            animation
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        can() {
            return this.$can('settings.finance');
        },
        invoices() {
            return useRepo(Invoice).where('company_id', this.currentCompany.id).orderBy('created_at', 'desc').get();
        },
        openInvoices() {
            // PENDING is not technically open. This means it's been sent to the bank and waiting for confirmation.
            return this.invoices.filter(invoice => ['BOOKED'/*, 'PENDING'*/, 'EXPIRED'].includes(invoice.state));
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY');
        },
        sendMail(invoice) {
            this.sendingMail = true;

            invoices.sendMail(invoice.id).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('settings.invoices.resent_invoice'),
                });

                this.sendingMail = false;
            });
        },
    },
    mounted() {
        this.fetchingInvoices = true;

        invoices.get(this.currentCompany.id).then(result => {
            return useRepo(Invoice).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingInvoices = false;
        });
    },
};
</script>
